import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMenuManagement } from "../../../context/MenuManagementContext";
import { isItemActive } from "./navMenuUtils";
import "./bottomnav.css";

const backdropStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1050,
  display: "flex",
};

const sheetStyle = {
  display: "flex",
  flexDirection: "column",
  zIndex: 1051,
  overflow: "hidden",
};

const ModuleTile = ({ item, active, onOpen, onNavigate }) => {
  const hasChildren = item.children && item.children.length > 0;

  const content = (
    <>
      <span className="mms-tile-icon">
        {item.icon && <i className={item.icon} />}
        {hasChildren && <span className="mms-tile-dot" />}
      </span>
      <span className="mms-tile-label">{item.title}</span>
    </>
  );

  if (!hasChildren) {
    return (
      <Link
        to={item.url || item.to || "#"}
        className={`mms-tile ${active ? "mms-tile--active" : ""}`}
        onClick={onNavigate}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className={`mms-tile ${active ? "mms-tile--active" : ""}`}
      onClick={() => onOpen(item.title)}
    >
      {content}
    </button>
  );
};

const ChildList = ({ module, currentPath, onNavigate }) => (
  <div className="mms-list">
    {module.children.map((child) => {
      const grandchildren = child.children && child.children.length > 0 ? child.children : null;

      if (!grandchildren) {
        const active = isItemActive(child, currentPath);
        return (
          <Link
            key={child.id ?? child.title}
            to={child.url || child.to || "#"}
            className={`mms-list-row ${active ? "mms-list-row--active" : ""}`}
            onClick={onNavigate}
          >
            {child.title}
          </Link>
        );
      }

      return (
        <React.Fragment key={child.id ?? child.title}>
          <div className="mms-section-label">{child.title}</div>
          {grandchildren.map((grandchild) => {
            const active = isItemActive(grandchild, currentPath);
            return (
              <Link
                key={grandchild.id ?? grandchild.title}
                to={grandchild.url || grandchild.to || "#"}
                className={`mms-list-row mms-list-row--nested ${active ? "mms-list-row--active" : ""}`}
                onClick={onNavigate}
              >
                {grandchild.title}
              </Link>
            );
          })}
        </React.Fragment>
      );
    })}
  </div>
);

const MoreMenuSheet = ({ open, onClose }) => {
  const { sideabarmenuDataList, sidebarLoading } = useMenuManagement();
  const [activeModuleTitle, setActiveModuleTitle] = useState(null);
  const location = useLocation();
  const firstPath = useRef(location.pathname);

  useEffect(() => {
    if (open && location.pathname !== firstPath.current) {
      onClose();
    }
    firstPath.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Always reopen fresh on the grid, never mid-drill-in from a previous session.
  useEffect(() => {
    if (open) setActiveModuleTitle(null);
  }, [open]);

  if (!open) return null;

  const menuItems = sideabarmenuDataList?.data || [];
  const activeModule = activeModuleTitle
    ? menuItems.find((m) => m.title === activeModuleTitle)
    : null;

  const handleClose = () => {
    setActiveModuleTitle(null);
    onClose();
  };

  return (
    <div className="mms-backdrop" style={backdropStyle} onClick={handleClose}>
      <div
        className="mms-sheet"
        style={sheetStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="All navigation modules"
      >
        <div className="mms-header">
          {activeModule ? (
            <button
              type="button"
              className="mms-back"
              onClick={() => setActiveModuleTitle(null)}
              aria-label="Back to all modules"
            >
              <i className="fa fa-chevron-left" />
            </button>
          ) : null}
          <h6 className="mms-header-title">{activeModule ? activeModule.title : "All Modules"}</h6>
          <button type="button" className="mms-close" onClick={handleClose} aria-label="Close">
            <i className="fa fa-times" />
          </button>
        </div>

        <div className="mms-body">
          {sidebarLoading ? (
            <div className="mms-loading">
              <div className="spinner-border spinner-border-sm" role="status" />
              <span>Loading menu...</span>
            </div>
          ) : menuItems.length === 0 ? (
            <div className="mms-empty">
              <i className="bi bi-grid-3x3-gap-fill" />
              <span>No modules available</span>
            </div>
          ) : activeModule ? (
            <ChildList module={activeModule} currentPath={location.pathname} onNavigate={handleClose} />
          ) : (
            <div className="mms-grid">
              {menuItems.map((item) => (
                <ModuleTile
                  key={item.id ?? item.title}
                  item={item}
                  active={isItemActive(item, location.pathname)}
                  onOpen={setActiveModuleTitle}
                  onNavigate={handleClose}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoreMenuSheet;
